'use client';

import { useState, useRef, useEffect } from 'react';
import { useTheme } from 'next-themes';
import { useGuardedRouter, useNavigationLeave, type LeaveOverride } from '@/shared/components/navigation-guard';
import Link from 'next/link';
import { Search, Building2, LogOut, User, ChevronDown, Menu, Sun, Moon, Monitor, HelpCircle, Sparkles, Check, Plus, Loader2, PanelLeftOpen } from 'lucide-react';
import { toast } from 'sonner';
import { useAuthStore } from '@/features/auth/stores/auth-store';
import { switchTenantErrorMessage } from '@/features/auth/lib/switch-error';
import { useSidebar } from './sidebar';
import { NotificationBell } from '@/features/notifications/components/notification-bell';
import { QuickCreateMenu } from '@/shared/components/quick-create-menu';
import { SetupChecklistTopbarTrigger } from '@/features/onboarding/components/setup-checklist-topbar-trigger';
import { useTour } from '@/features/tour/hooks/use-product-tour';
import { PageBannerOverlay } from './page-banner-overlay';
// HelpMenu was a dedicated topbar popover. Folded into UserMenu on 2026-04-26
// to declutter the topbar (now: "Centru de ajutor" link below).

function getInitials(name: string): string {
  return name
    .split(' ')
    .map((part) => part[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
}

export function TopBar() {
  const router = useGuardedRouter();
  const { requestLeave } = useNavigationLeave();
  const { user, company, tenants, logout, switchTenant } = useAuthStore();
  const { toggle } = useSidebar();
  const [switchingTo, setSwitchingTo] = useState<string | null>(null);
  const { theme, setTheme } = useTheme();
  const [mounted, setMounted] = useState(false);

  useEffect(() => setMounted(true), []);
  const [showUserMenu, setShowUserMenu] = useState(false);

  // Sidebar collapsed state — păstrăm o copie locală sincronizată cu sidebar
  // prin event-ul `sf-sidebar:state-changed`. Când e colapsat, randăm un buton
  // de expand în topbar (langa hamburger). Default false (sidebar pornește expandat).
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  useEffect(() => {
    function onStateChanged(e: Event) {
      const ev = e as CustomEvent<{ collapsed?: boolean }>;
      if (typeof ev.detail?.collapsed === 'boolean') setSidebarCollapsed(ev.detail.collapsed);
    }
    window.addEventListener('sf-sidebar:state-changed', onStateChanged);
    return () => window.removeEventListener('sf-sidebar:state-changed', onStateChanged);
  }, []);

  const expandSidebar = () => {
    window.dispatchEvent(
      new CustomEvent('sf-sidebar:set-collapsed', { detail: { collapsed: false } }),
    );
  };

  const overviewTour = useTour('overview');
  const firstInvoiceTour = useTour('first-invoice');
  const nirTour = useTour('nir-reception');
  const efacturaTour = useTour('efactura-setup');
  const expenseTour = useTour('expense-new');
  const importTour = useTour('data-import');
  const paymentTour = useTour('payment-receive');
  const menuRef = useRef<HTMLDivElement>(null);
  const menuButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setShowUserMenu(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close on Escape
  useEffect(() => {
    function handleEscape(event: KeyboardEvent) {
      if (event.key === 'Escape') setShowUserMenu(false);
    }
    if (showUserMenu) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [showUserMenu]);

  // Plecare din meniu cu un formular nesalvat: întâi dialogul aplicației („Continuă editarea”
  // păstrează sesiunea). Meniul se închide înainte, iar focusul trece pe butonul lui, unde dialogul
  // îl întoarce la anulare (elementul din meniu nu mai există).
  const leaveFromMenu = (proceed: () => void, override?: LeaveOverride) => {
    menuButtonRef.current?.focus();
    setShowUserMenu(false);
    requestLeave(proceed, override);
  };

  // La deconectare se șterg și ciornele locale, deci mesajul blocului („ciorna se păstrează”) nu e valabil.
  const handleLogout = () =>
    leaveFromMenu(() => void logout(), {
      message: 'Dacă te deconectezi acum, modificările nesalvate se pierd.',
      actionLabel: 'Deconectează-te',
    });

  const switchTo = async (tenantId: string) => {
    setSwitchingTo(tenantId);
    try {
      await switchTenant(tenantId);
      // switchTenant face hard reload — codul de jos nu ruleaza
    } catch (e) {
      setSwitchingTo(null);
      // Stringify pentru debug — empty `{}` la consolă apare când
      // request-ul eșuează la nivel proxy (API restart) și body-ul
      // e gol; JSON.stringify expune statusCode/message dacă există.
      const detail = (() => {
        try { return JSON.stringify(e); } catch { return String(e); }
      })();
      console.error('Switch tenant failed', e, 'detail:', detail);
      // Refuzul de plan plătit l-a afișat deja clientul API (cu „Vezi planurile”): nu-l repetăm.
      const msg = switchTenantErrorMessage(e);
      if (msg) toast.error(msg);
    }
  };

  const displayName = user?.name || 'Utilizator';
  const displayEmail = user?.email || '';
  const initials = user?.name ? getInitials(user.name) : 'U';
  const companyName = company?.name || 'Firma Mea';

  return (
    <header className="relative flex h-16 items-center justify-between border-b border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-900 px-4 sm:px-6">
      {/* Left section */}
      <div className="flex items-center gap-3 flex-1">
        {/* Hamburger — mobile only */}
        <button
          onClick={toggle}
          className="lg:hidden flex h-10 w-10 items-center justify-center rounded-lg text-neutral-600 dark:text-neutral-400 hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors"
          aria-label="Deschide meniul"
        >
          <Menu className="h-5 w-5" />
        </button>

        {/* Sidebar expand toggle — desktop, vizibil doar când sidebar e colapsat */}
        {sidebarCollapsed && (
          <button
            onClick={expandSidebar}
            className="hidden lg:inline-flex h-10 w-10 items-center justify-center rounded-lg text-neutral-500 dark:text-neutral-400 hover:text-primary hover:bg-primary/5 transition-colors"
            aria-label="Extinde meniul"
            title="Extinde meniul"
          >
            <PanelLeftOpen className="h-5 w-5" />
          </button>
        )}

        {/* Mobile search icon */}
        <button
          onClick={() => window.dispatchEvent(new Event('open-command-palette'))}
          className="sm:hidden flex h-10 w-10 items-center justify-center rounded-lg text-neutral-500 dark:text-neutral-400 hover:bg-neutral-50 dark:hover:bg-neutral-800 transition-colors"
        >
          <Search className="h-5 w-5" />
        </button>
      </div>

      {/* Center — Search: absolute, centrat în HEADER (care e `relative`). NU `fixed`
          la viewport — altfel rămâne lipit la 12px de marginea ferestrei și se
          suprapune peste bannerele de deasupra header-ului (trial, grant, CAEN...). */}
      <div className="hidden sm:block absolute max-w-md px-4 z-10" style={{ left: "50%", top: "50%", transform: "translate(-50%, -50%)" }}>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-400" />
          <input
            type="text"
            readOnly
            placeholder="Caută facturi, clienți..."
            onClick={() => window.dispatchEvent(new Event('open-command-palette'))}
            style={{ outline: 'none' }}
            className="w-full rounded-lg border border-neutral-200 dark:border-neutral-700 bg-neutral-50 dark:bg-neutral-950 py-2.5 pl-10 pr-16 text-sm text-neutral-700 dark:text-neutral-300 placeholder:text-neutral-400 dark:placeholder:text-neutral-500 hover:border-neutral-300 dark:hover:border-neutral-600 cursor-pointer focus:outline-none focus:ring-0 focus-visible:ring-0 focus-visible:ring-offset-0 focus:border-neutral-200 dark:focus:border-neutral-700 transition-all duration-150"
          />
          <kbd className="absolute right-3 top-1/2 -translate-y-1/2 rounded border border-neutral-200 dark:border-neutral-600 px-1.5 py-0.5 text-[0.625rem] text-neutral-400 tabular-nums pointer-events-none">Ctrl+K</kbd>
        </div>
      </div>

      {/* Right section */}
      <div className="flex items-center gap-2 sm:gap-3">
        {/* Quick create — primary action, leftmost slot for prominence */}
        <QuickCreateMenu />

        {/* Setup checklist (auto-hides at 100%) */}
        <div data-tour="topbar-setup-checklist" className="flex">
          <SetupChecklistTopbarTrigger />
        </div>

        {/* Account cluster — bell + avatar grouped under one envelope (gap-1
           instead of gap-3, subtle hover bg over the whole zone). Notification
           badge stays visible; the cluster signals "user-actions zone" without
           merging two dropdowns. */}
        <div className="flex items-center gap-1 rounded-xl px-1 -mx-1 transition-colors hover:bg-neutral-50/60 dark:hover:bg-neutral-800/40">
          <NotificationBell />

          {/* User menu */}
          <div className="relative" ref={menuRef}>
          <button
            ref={menuButtonRef}
            onClick={() => setShowUserMenu(!showUserMenu)}
            className="flex items-center gap-2.5 rounded-lg px-2 py-1.5 hover:bg-neutral-50 dark:hover:bg-neutral-800 transition-colors"
            aria-expanded={showUserMenu}
            aria-haspopup="true"
          >
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary text-sm font-medium text-white">
              {initials}
            </div>
            <div className="hidden sm:block text-left min-w-0">
              <p className="text-sm font-medium text-neutral-900 dark:text-neutral-100 truncate max-w-[200px]">{companyName}</p>
              <p className="text-xs text-neutral-400 truncate max-w-[200px]">{displayName}</p>
            </div>
            {switchingTo ? (
              <Loader2 className="h-4 w-4 text-neutral-400 shrink-0 animate-spin" aria-hidden="true" />
            ) : (
              <ChevronDown className={`hidden sm:block h-4 w-4 text-neutral-400 shrink-0 transition-transform duration-200 ${showUserMenu ? 'rotate-180' : ''}`} />
            )}
            <span className="sr-only" aria-live="polite">{switchingTo ? 'Se schimbă firma…' : ''}</span>
          </button>

          {showUserMenu && (
            <div className="absolute right-0 top-full mt-2 w-72 max-w-[calc(100vw-1rem)] max-h-[calc(100vh-5rem)] overflow-y-auto rounded-xl border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-900 py-1 shadow-lg dark:shadow-neutral-900/50 z-50 animate-scale-in origin-top-right">
              {/* User + company info */}
              <div className="border-b border-neutral-100 dark:border-neutral-800 px-4 py-3">
                <div className="flex items-center gap-2 mb-1">
                  <Building2 className="h-3.5 w-3.5 text-neutral-400 shrink-0" />
                  <p className="text-sm font-semibold text-neutral-900 dark:text-neutral-100">{companyName}</p>
                </div>
                <p className="text-sm text-neutral-700 dark:text-neutral-300">{displayName}</p>
                <p className="text-xs text-neutral-500 dark:text-neutral-400 truncate">{displayEmail}</p>
                {company?.cui && (
                  <p className="text-xs text-neutral-400 mt-0.5">CUI: <span className="tabular-nums">{company.cui}</span></p>
                )}
              </div>

              {/* Firmele mele — switcher (afisat doar daca user are 2+ firme) */}
              {tenants.length >= 2 && (
                <div className="border-b border-neutral-100 dark:border-neutral-800 py-1">
                  <div className="px-4 pt-2 pb-1 flex items-center justify-between">
                    <p className="text-[0.6875rem] font-medium text-neutral-400 uppercase tracking-wider">Firmele mele</p>
                    <button
                      onClick={() => {
                        setShowUserMenu(false);
                        router.push('/setari?tab=firmele-mele');
                      }}
                      title="Adaugă firma noua"
                      className="text-neutral-400 hover:text-primary transition-colors"
                    >
                      <Plus className="h-3.5 w-3.5" />
                    </button>
                  </div>
                  <div className="max-h-56 overflow-y-auto">
                    {tenants.map((t) => {
                      const isActive = t.id === company?.id;
                      const isLoading = switchingTo === t.id;
                      return (
                        <button
                          key={t.id}
                          onClick={() => {
                            if (isActive || switchingTo) return;
                            // Formularul nesalvat al firmei curente: întâi dialogul aplicației.
                            leaveFromMenu(() => void switchTo(t.id), { actionLabel: 'Schimbă firma' });
                          }}
                          disabled={isActive || !!switchingTo}
                          className={`w-full flex items-center gap-3 px-4 py-2 text-left transition-colors ${
                            isActive
                              ? 'bg-primary/[0.06] dark:bg-primary/[0.10] cursor-default'
                              : 'hover:bg-neutral-50 dark:hover:bg-neutral-800/60'
                          } ${switchingTo && !isLoading ? 'opacity-50' : ''}`}
                        >
                          <Building2 className={`h-4 w-4 shrink-0 ${isActive ? 'text-primary' : 'text-neutral-400'}`} />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-neutral-900 dark:text-neutral-100 truncate">{t.name}</p>
                            {t.cui && <p className="text-[0.6875rem] text-neutral-400 truncate">CUI: {t.cui}</p>}
                          </div>
                          {isLoading ? (
                            <Loader2 className="h-4 w-4 text-primary animate-spin shrink-0" />
                          ) : isActive ? (
                            <Check className="h-4 w-4 text-primary shrink-0" />
                          ) : null}
                        </button>
                      );
                    })}
                  </div>
                  <button
                    onClick={() => {
                      setShowUserMenu(false);
                      router.push('/setari?tab=firmele-mele');
                    }}
                    className="w-full px-4 py-1.5 text-[0.6875rem] text-neutral-500 dark:text-neutral-400 hover:text-primary transition-colors text-left"
                  >
                    Toate firmele →
                  </button>
                </div>
              )}

              {/* Theme selector */}
              {mounted && (
              <div className="border-b border-neutral-100 dark:border-neutral-800 px-4 py-3">
                <p className="text-[0.6875rem] font-medium text-neutral-400 uppercase tracking-wider mb-2">Tema</p>
                <div className="flex items-center gap-1 p-0.5 bg-neutral-100 dark:bg-neutral-800 rounded-lg">
                  {([
                    { value: 'light', label: 'Luminos', icon: Sun },
                    { value: 'dark', label: 'Întunecat', icon: Moon },
                    { value: 'system', label: 'Sistem', icon: Monitor },
                  ] as const).map(opt => (
                    <button
                      key={opt.value}
                      onClick={() => setTheme(opt.value)}
                      className={`flex-1 flex items-center justify-center gap-1.5 rounded-md px-2 py-1.5 text-xs font-medium transition-all ${
                        theme === opt.value
                          ? 'bg-white dark:bg-neutral-700 text-neutral-900 dark:text-neutral-100 shadow-sm'
                          : 'text-neutral-500 dark:text-neutral-400 hover:text-neutral-700 dark:hover:text-neutral-300'
                      }`}
                    >
                      <opt.icon className="h-3.5 w-3.5" />
                      <span className="hidden sm:inline">{opt.label}</span>
                    </button>
                  ))}
                </div>
              </div>
              )}

              {/* Menu items */}
              <div className="py-1">
                <button
                  onClick={() => {
                    setShowUserMenu(false);
                    router.push('/ajutor');
                  }}
                  className="flex w-full items-center gap-3 px-4 py-2.5 text-sm text-neutral-700 dark:text-neutral-300 hover:bg-neutral-50 dark:hover:bg-neutral-800 transition-colors"
                >
                  <HelpCircle className="h-4 w-4 text-neutral-400" />
                  Centru de ajutor
                </button>
                <button
                  onClick={() => {
                    setShowUserMenu(false);
                    router.push('/setari');
                  }}
                  className="flex w-full items-center gap-3 px-4 py-2.5 text-sm text-neutral-700 dark:text-neutral-300 hover:bg-neutral-50 dark:hover:bg-neutral-800 transition-colors"
                >
                  <User className="h-4 w-4 text-neutral-400" />
                  Setari cont
                </button>

                {/* Tururi ghidate */}
                <div className="mt-1 mb-0.5 px-4 pt-2 text-[0.625rem] font-semibold uppercase tracking-wider text-neutral-400 dark:text-neutral-500">
                  Tururi ghidate
                </div>
                <button
                  onClick={() => {
                    setShowUserMenu(false);
                    void overviewTour.restart();
                  }}
                  className="flex w-full items-center gap-3 px-4 py-2.5 text-sm text-neutral-700 dark:text-neutral-300 hover:bg-amber-50/50 dark:hover:bg-amber-950/20 transition-colors"
                >
                  <Sparkles className="h-4 w-4 text-amber-500" />
                  Tur: meniuri principale
                </button>
                <button
                  onClick={() => {
                    setShowUserMenu(false);
                    void firstInvoiceTour.restart();
                    router.push('/facturi/nou');
                  }}
                  className="flex w-full items-center gap-3 px-4 py-2.5 text-sm text-neutral-700 dark:text-neutral-300 hover:bg-amber-50/50 dark:hover:bg-amber-950/20 transition-colors"
                >
                  <Sparkles className="h-4 w-4 text-amber-500" />
                  Tur: prima factură
                </button>
                <button
                  onClick={() => {
                    setShowUserMenu(false);
                    void nirTour.restart();
                    router.push('/receptie/nou');
                  }}
                  className="flex w-full items-center gap-3 px-4 py-2.5 text-sm text-neutral-700 dark:text-neutral-300 hover:bg-amber-50/50 dark:hover:bg-amber-950/20 transition-colors"
                >
                  <Sparkles className="h-4 w-4 text-amber-500" />
                  Tur: recepție NIR
                </button>
                <button
                  onClick={() => {
                    setShowUserMenu(false);
                    void efacturaTour.restart();
                    router.push('/setari?tab=efactura');
                  }}
                  className="flex w-full items-center gap-3 px-4 py-2.5 text-sm text-neutral-700 dark:text-neutral-300 hover:bg-amber-50/50 dark:hover:bg-amber-950/20 transition-colors"
                >
                  <Sparkles className="h-4 w-4 text-amber-500" />
                  Tur: e-Factura ANAF
                </button>
                <button
                  onClick={() => {
                    setShowUserMenu(false);
                    void expenseTour.restart();
                    // Etapa 2: ancorele turului trăiesc în modalul rapid → lista îl
                    // auto-deschide din ?nou=1 (fostul /cheltuieli/nou e redirect).
                    router.push('/cheltuieli?nou=1');
                  }}
                  className="flex w-full items-center gap-3 px-4 py-2.5 text-sm text-neutral-700 dark:text-neutral-300 hover:bg-amber-50/50 dark:hover:bg-amber-950/20 transition-colors"
                >
                  <Sparkles className="h-4 w-4 text-amber-500" />
                  Tur: înregistrare cheltuială
                </button>
                <button
                  onClick={() => {
                    setShowUserMenu(false);
                    void importTour.restart();
                    router.push('/migrare');
                  }}
                  className="flex w-full items-center gap-3 px-4 py-2.5 text-sm text-neutral-700 dark:text-neutral-300 hover:bg-amber-50/50 dark:hover:bg-amber-950/20 transition-colors"
                >
                  <Sparkles className="h-4 w-4 text-amber-500" />
                  Tur: import date
                </button>
                <button
                  onClick={() => {
                    setShowUserMenu(false);
                    void paymentTour.restart();
                    router.push('/incasari');
                  }}
                  className="flex w-full items-center gap-3 px-4 py-2.5 text-sm text-neutral-700 dark:text-neutral-300 hover:bg-amber-50/50 dark:hover:bg-amber-950/20 transition-colors"
                >
                  <Sparkles className="h-4 w-4 text-amber-500" />
                  Tur: înregistrare încasare
                </button>
              </div>

              {/* Logout */}
              <div className="border-t border-neutral-100 dark:border-neutral-800 py-1">
                <button
                  onClick={handleLogout}
                  className="flex w-full items-center gap-3 px-4 py-2.5 text-sm text-danger-600 dark:text-danger-400 hover:bg-danger-50 dark:hover:bg-danger-900/20 transition-colors"
                >
                  <LogOut className="h-4 w-4" />
                  Deconectare
                </button>
              </div>
            </div>
          )}
          </div>
        </div>
      </div>

      {/* Banner overlay — slide-in din dreapta peste secțiunea companiei.
          Triggerat de pageBanner.show(...) în loc de toast.success(...). */}
      <PageBannerOverlay />
    </header>
  );
}
